import { createEffect, createMemo, createSignal, For, Show, type Accessor, type JSX } from "solid-js"
import { Button } from "@openanalyst-ai/ui/button"
import { Card } from "@openanalyst-ai/ui/card"
import { Icon } from "@openanalyst-ai/ui/icon"
import { Markdown } from "@openanalyst-ai/ui/markdown"
import { StatWidget } from "@/components/dashboard/widget-stat"
import { ChartWidget } from "@/components/dashboard/widget-chart"
import { TableWidget } from "@/components/dashboard/widget-table"
import { ListWidget } from "@/components/dashboard/widget-list"
import { DetailWidget } from "@/components/dashboard/widget-detail"
import { FormWidget } from "@/components/dashboard/widget-form"
import { GenerativeBlockView, GenerationStageVisual, type GenerativeBlock } from "./generative-block"
import { RegistryBlock as RegistryBlockRenderer } from "./registry-block"
import type { PluginRegistryBlock } from "@openanalyst-ai/plugin"
import { VisualPipelineBlockView, type VisualPipelineBlock } from "./visual-pipeline-block"
import { evaluatePluginCondition, pluginConditionAllowed } from "./json-logic"
import { mergedDashboardReferences, type DashboardComponentIndex } from "./dashboard-component-index"
import { redactPluginPageView } from "./plugin-redaction"

type ActionState = {
  pending?: boolean
  error?: string
  result?: unknown
}

type SubmitState = {
  pending?: boolean
  error?: string
  result?: unknown
}

type WidgetActionDeclaration = {
  id: string
  label: string
  toolName: string
  args?: Record<string, unknown>
  variant?: "primary" | "secondary" | "ghost"
  risk?: "read" | "local_write" | "external_write" | "destructive"
  confirm?: {
    title?: string
    description?: string
    sideEffect?: string
    reversibility?: string
    payloadSummary?: string
  }
  visibleWhen?: unknown
  disabledWhen?: unknown
  disabledReason?: string
  refreshWidgetIds?: string[]
  input?: Record<
    string,
    {
      source: "view" | "block" | "row" | "values" | "args"
      path: string
      fallback?: unknown
    }
  >
}

type ArtifactActionDeclaration = {
  id: string
  label: string
  toolName: string
  contexts: Array<"file" | "selection">
  intent: "remember" | "publish" | "validate" | "export" | "open" | "other"
  risk: "read" | "local_write" | "external_write" | "destructive"
  match?: {
    extensions?: string[]
    pathPrefixes?: string[]
    mimeTypes?: string[]
  }
  args?: Record<string, unknown>
  description?: string
  destinationLabel?: string
  authRequired?: boolean
  confirm?: {
    title?: string
    description?: string
    sideEffect?: string
    reversibility?: string
    payloadSummary?: string
  }
}

type WidgetFieldDeclaration = {
  key: string
  label: string
  type: "text" | "textarea" | "select" | "toggle" | "number" | "password" | "date" | "month"
  required?: boolean
  placeholder?: string
  description?: string
  sensitive?: boolean
  min?: number
  max?: number
  step?: number
  options?: Array<{ label: string; value: string }>
  visibleWhen?: unknown
  disabledWhen?: unknown
}

type EmptyStateDeclaration = {
  title?: string
  description?: string
}

type FormSectionDeclaration = {
  title?: string
  description?: string
  fields: WidgetFieldDeclaration[]
  visibleWhen?: unknown
}

type HeroBlock = {
  id: string
  type: "hero"
  eyebrow?: string
  title: string
  description?: string
  imageUrl?: string
}

type MarkdownBlock = {
  id: string
  type: "markdown"
  content: string
}

type StatsBlock = {
  id: string
  type: "stats"
  title?: string
  items: Array<{ label: string; value: string; change: string; trend: "up" | "down" }>
  actions?: WidgetActionDeclaration[]
}

type ChartBlock = {
  id: string
  type: "chart"
  title?: string
  chartType?: "line" | "bar" | "doughnut" | "pie" | "radar" | "polarArea"
  data: {
    labels: string[]
    series: Array<{ key: string; label: string; data: number[]; color?: string }>
  }
  options?: Record<string, unknown>
}

type TableBlock = {
  id: string
  type: "table"
  title?: string
  columns: Array<{
    key: string
    label: string
    sortable?: boolean
    width?: string
    align?: "left" | "center" | "right"
    format?: "text" | "status" | "code" | "date" | "datetime" | "currency" | "url" | "boolean" | "json"
  }>
  rows: Record<string, unknown>[]
  rowIdKey?: string
  rowActions?: WidgetActionDeclaration[]
  expandedRows?: Record<string, PluginBlock[]>
  emptyState?: EmptyStateDeclaration
}

type ListBlock = {
  id: string
  type: "list"
  title?: string
  items: Array<{
    id?: string
    title: string
    description?: string
    status: "success" | "info" | "warning" | "error"
    timestamp: string
    href?: string
  }>
  actions?: WidgetActionDeclaration[]
  itemActions?: WidgetActionDeclaration[]
  emptyState?: EmptyStateDeclaration
}

type DetailBlock = {
  id: string
  type: "detail"
  title?: string
  sections: Array<{
    title?: string
    items: Array<{ label: string; value: unknown; hint?: string }>
  }>
  actions?: WidgetActionDeclaration[]
}

type FormBlock = {
  id: string
  type: "form"
  title?: string
  fields: WidgetFieldDeclaration[]
  sections?: FormSectionDeclaration[]
  values?: Record<string, unknown>
  note?: string
  submitToolName: string
  submitLabel?: string
  submitAction?: Omit<WidgetActionDeclaration, "toolName">
}

type CalendarItem = {
  id: string
  title: string
  startDate: string
  endDate: string
  status?: string
  channel?: string
  meta?: Record<string, unknown>
}

type CalendarBlock = {
  id: string
  type: "calendar"
  title?: string
  visibleMonth: string
  items: CalendarItem[]
  statuses?: Array<{ id: string; label: string; color?: string; background?: string }>
  channels?: Array<{ id: string; label: string; color?: string }>
  editForm?: {
    fields: WidgetFieldDeclaration[]
    sections?: FormSectionDeclaration[]
    note?: string
    submitToolName: string
    submitLabel?: string
    submitAction?: Omit<WidgetActionDeclaration, "toolName">
  }
  actions?: WidgetActionDeclaration[]
  source?: Record<string, unknown>
  diagnostics?: Array<{ message: string; tone?: "info" | "success" | "warning" | "error" }>
  emptyState?: EmptyStateDeclaration
}

type TodoBlock = {
  id: string
  type: "todo"
  title?: string
  submitToolName: string
  items: Array<{
    id: string
    title: string
    status: "todo" | "doing" | "done" | "blocked"
    priority?: "low" | "medium" | "high"
    dueAt?: string
    source?: string
  }>
}

type CardGridBlock = {
  id: string
  type: "card_grid"
  title?: string
  items: Array<{
    id: string
    title: string
    description?: string
    imageUrl?: string
    mediaKind?: "image" | "video" | string
    eyebrow?: string
    href?: string
  }>
  actions?: WidgetActionDeclaration[]
  itemActions?: WidgetActionDeclaration[]
  emptyState?: EmptyStateDeclaration
}

type NoticeBlock = {
  id: string
  type: "notice"
  title?: string
  message: string
  tone?: "info" | "success" | "warning" | "error"
  actions?: WidgetActionDeclaration[]
}

type PreviewBlock = {
  id: string
  type: "preview"
  title?: string
  mode: "html" | "markdown" | "url" | "json"
  content: unknown
  emptyState?: EmptyStateDeclaration
}

type GalleryBlock = {
  id: string
  type: "gallery"
  title?: string
  items: Array<{
    src: string
    alt?: string
    title?: string
    description?: string
  }>
}




type PluginSetupCredential = {
  key: string
  label: string
  kind?: "api_key" | "access_token" | "connection_token" | "oauth" | "text"
  required?: boolean
  configured?: boolean
  verified?: boolean
  description?: string
  placeholder?: string
}

type PluginSetupConnection = {
  id: string
  label: string
  status: "missing" | "needs_credentials" | "verifying" | "connected" | "warning" | "error"
  active?: boolean
  description?: string
  lastVerifiedAt?: string
  actions?: WidgetActionDeclaration[]
}

type PluginSetupForm = {
  fields?: WidgetFieldDeclaration[]
  sections?: FormSectionDeclaration[]
  values?: Record<string, unknown>
  note?: string
  submitToolName: string
  submitLabel?: string
  submitAction?: Omit<WidgetActionDeclaration, "toolName">
}

export type PluginPageSetup = {
  status?: "idle" | "needs_credentials" | "verifying" | "connected" | "warning" | "error"
  title?: string
  description?: string
  provider?: string
  activeConnectionId?: string
  credentials?: PluginSetupCredential[]
  connections?: PluginSetupConnection[]
  form?: PluginSetupForm
  actions?: WidgetActionDeclaration[]
}

export type PluginDashboardDeclaration = {
  title?: string
  description?: string
  display?: "auto" | "hidden" | "compact"
  layout?: {
    locked?: boolean
    lockedAt?: string
    lockedReason?: string
  }
  componentIndex?: DashboardComponentIndex
  defaults?: {
    system?: DashboardDefault
    user?: DashboardDefault
  }
  customization?: {
    mode?: "system" | "user" | "custom"
    activeDefaultId?: string
    hasUserDefault?: boolean
    resettable?: boolean
    updatedAt?: string
  }
  actions?: WidgetActionDeclaration[]
}

type DashboardOverlay = {
  version?: string
  title?: string
  description?: string
  blockOrder?: string[]
  hiddenBlockIds?: string[]
  blockOverrides?: Record<string, Record<string, unknown>>
  componentIndex?: DashboardComponentIndex
}

type DashboardDefault = {
  id: string
  label: string
  scope: "system" | "user"
  description?: string
  overlay?: DashboardOverlay
  createdAt?: string
  updatedAt?: string
}

// Local mirror of the protocol's PluginRegistryBlock so view-renderer
// can dispatch on it. Kept structurally identical to the schema at
// packages/plugin/src/index.ts so a casted PluginBlock from either
// source resolves.
//
// Declared as a `type` alias (not `interface`) so TypeScript can treat
// it as compatible with `Record<string, unknown>` where the rest of
// the PluginBlock union members are inferred from Zod schemas.
export type RegistryBlock = {
  id: string
  type: "registry"
  componentId: string
  version: string
  intent?: string
  title?: string
  span?: "auto" | "full"
  props?: Record<string, unknown>
  slots?: Record<string, RegistrySlot>
  actions?: WidgetActionDeclaration[]
  submitToolName?: string
  submits?: Array<{
    id: string
    label: string
    command: string
    variant?: "primary" | "secondary" | "ghost"
    disabledReason?: string
  }>
}

type RegistryNode = {
  componentId: string
  version: string
  intent?: string
  props?: Record<string, unknown>
  slots?: Record<string, RegistrySlot>
}

type RegistrySlotItem = string | number | boolean | RegistryNode
type RegistrySlot = RegistrySlotItem | RegistrySlotItem[]

// The old artifact-editor route was removed; a legacy `artifact_editor`
// block now renders a read-only preview. Keep a minimal local type so the block
// union still type-checks without the deleted component module.
type ArtifactEditorBlock = {
  id: string
  type: "artifact_editor"
  title?: string
  html: string
  artifactPath: string
  checksum: string
  qualityStatus: "unchecked" | "passed" | "failed"
  snapshots: string[]
  submitToolName: string
  visualUnavailableReason?: string
}

type GenerativeStageBlock = {
  id: string
  type: "generative_stage"
  title?: string
  job: Record<string, unknown>
}

export type PluginBlock =
  | HeroBlock
  | MarkdownBlock
  | StatsBlock
  | CalendarBlock
  | ChartBlock
  | TableBlock
  | ListBlock
  | DetailBlock
  | FormBlock
  | TodoBlock
  | CardGridBlock
  | GalleryBlock
  | GenerativeBlock
  | GenerativeStageBlock
  | ArtifactEditorBlock
  | VisualPipelineBlock
  | NoticeBlock
  | PreviewBlock
  | RegistryBlock

export type PluginPageView = {
  title: string
  description?: string
  layout?: "stack" | "grid" | "masonry"
  home?: {
    title?: string
    description?: string
    menu?: string[]
    banner?: {
      items?: Array<{
        type?: "image" | "video" | "youtube" | "embed"
        src?: string
        url?: string
        poster?: string
        alt?: string
        title?: string
      }>
      images?: string[]
      intervalMs?: number
      fit?: "cover" | "contain"
    }
    about?: {
      title?: string
      description?: string
      tools?: Array<string | { label: string; description?: string }>
    }
    tools?: Array<string | { label: string; description?: string }>
    contact?: {
      label?: string
      email?: string
      description?: string
    }
  }
  setup?: PluginPageSetup
  dashboard?: PluginDashboardDeclaration
  artifactActions?: ArtifactActionDeclaration[]
  blocks: PluginBlock[]
  accountBlocks?: PluginBlock[]
  toolsBlocks?: PluginBlock[]
  canvasBlocks?: PluginBlock[]
  canvasHtml?: string
  canvasVersions?: { id: string; name: string; createdAt: number; active?: boolean; published?: boolean }[]
}

export type PluginViewState = {
  pendingActionIds: ReadonlySet<string>
  pendingSubmitIds: ReadonlySet<string>
  actionState?: Record<string, Omit<ActionState, "pending">>
  submitState?: Record<string, Omit<SubmitState, "pending">>
}

export type PluginActionRequest = {
  actionId: string
  blockId?: string
  rowId?: string
  confirmed?: boolean
}

export function pluginActionStateKey(input: Pick<PluginActionRequest, "actionId" | "blockId" | "rowId">) {
  return [input.blockId ?? "", input.rowId ?? "", input.actionId].join(":")
}

export const SETUP_BLOCK_ID = "__plugin_setup"
const DASHBOARD_BLOCK_ID = "__plugin_dashboard"

type PluginBrand = {
  accent: string
  tint: string
  wash: string
  ink: string
}

type PluginConnectionTone = "connected" | "disconnected" | "checking" | "warning" | "idle"

type PluginConnectionState = {
  tone: PluginConnectionTone
  label: string
}

const DEFAULT_PLUGIN_BRAND: PluginBrand = {
  accent: "#f26d3f",
  tint: "rgba(242,109,63,0.14)",
  wash: "rgba(242,109,63,0.055)",
  ink: "#9a3412",
}

const PLUGIN_BRANDS: Array<{ tokens: string[]; brand: PluginBrand }> = [
  {
    tokens: ["meta ads", "facebook ads"],
    brand: {
      accent: "#f26d3f",
      tint: "rgba(242,109,63,0.15)",
      wash: "rgba(242,109,63,0.06)",
      ink: "#9a3412",
    },
  },
  {
    tokens: ["shopify"],
    brand: {
      accent: "#5f8f2f",
      tint: "rgba(95,143,47,0.15)",
      wash: "rgba(95,143,47,0.055)",
      ink: "#365314",
    },
  },
  {
    tokens: ["marketing planner", "calendar"],
    brand: {
      accent: "#e37400",
      tint: "rgba(227,116,0,0.15)",
      wash: "rgba(227,116,0,0.055)",
      ink: "#92400e",
    },
  },
  {
    tokens: ["dashboard"],
    brand: {
      accent: "#0f766e",
      tint: "rgba(15,118,110,0.14)",
      wash: "rgba(15,118,110,0.055)",
      ink: "#134e4a",
    },
  },
  {
    tokens: ["crm"],
    brand: {
      accent: "#0d9488",
      tint: "rgba(13,148,136,0.14)",
      wash: "rgba(13,148,136,0.055)",
      ink: "#115e59",
    },
  },
  {
    tokens: ["generative", "studio"],
    brand: {
      accent: "#db2777",
      tint: "rgba(219,39,119,0.13)",
      wash: "rgba(219,39,119,0.045)",
      ink: "#9d174d",
    },
  },
  {
    tokens: ["artifact editor"],
    brand: {
      accent: "#2563eb",
      tint: "rgba(37,99,235,0.13)",
      wash: "rgba(37,99,235,0.045)",
      ink: "#1e40af",
    },
  },
  {
    tokens: ["10xmemory", "10x memory", "memory"],
    brand: {
      accent: "#7c3aed",
      tint: "rgba(124,58,237,0.12)",
      wash: "rgba(124,58,237,0.04)",
      ink: "#5b21b6",
    },
  },
  {
    tokens: ["publishing"],
    brand: {
      accent: "#d97706",
      tint: "rgba(217,119,6,0.15)",
      wash: "rgba(217,119,6,0.055)",
      ink: "#92400e",
    },
  },
]

export function PluginViewRenderer(props: {
  view: PluginPageView
  state: PluginViewState
  onAction: (request: PluginActionRequest) => void | Promise<void>
  onSubmit: (blockId: string, values: Record<string, unknown>, confirmed?: boolean) => void | Promise<void>
  onUpload?: (blockId: string, values: { file: File; studio: string; kind?: string }) => Promise<unknown>
  onLayoutLockChange?: (locked: boolean) => void
  readonly?: boolean
  // Chrome mode. "default" (omitted) renders the legacy PluginShellHeader.
  // "none" suppresses it so an outer shell (for example an embedded app host)
  // the pluginCanvas flag) can own the header and outer scroll container.
  chrome?: "default" | "none"
}) {
  const blockSlots = new Map<string, ReturnType<typeof createBlockSlot>>()
  const layout = createMemo(() =>
    props.view.layout === "stack"
      ? "flex flex-col gap-4"
      : props.view.layout === "masonry"
        ? "columns-1 xl:columns-2 gap-4 space-y-4"
        : "grid grid-cols-1 items-stretch gap-4 xl:grid-cols-2",
  )
  const chromeless = createMemo(() => props.chrome === "none")

  return (
    <div
      classList={{
        "w-full flex-1 min-h-0 overflow-y-auto px-6 py-5": !chromeless(),
        "w-full": chromeless(),
      }}
    >
      <div
        classList={{
          "mx-auto flex w-full max-w-7xl flex-col gap-6": !chromeless(),
          "flex w-full flex-col gap-6": chromeless(),
        }}
      >
        <Show when={!chromeless()}>
          <PluginShellHeader
            view={props.view}
            state={props.state}
            readonly={props.readonly}
            onAction={props.onAction}
            onSubmit={props.onSubmit}
            onLayoutLockChange={props.onLayoutLockChange}
          />
        </Show>

        <div class={layout()}>
          <For each={stableBlocks(props.view.blocks, blockSlots)}>
            {(block) =>
              renderBlock(
                props.view,
                block.value,
                props.state,
                props.onAction,
                props.onSubmit,
                props.onUpload,
                props.readonly,
              )
            }
          </For>
        </div>
      </div>
    </div>
  )
}

function pluginBrand(view: PluginPageView) {
  const search = [
    view.title,
    view.description,
    ...view.blocks.flatMap((block) => ("title" in block && typeof block.title === "string" ? [block.title] : [])),
  ]
    .join(" ")
    .toLowerCase()
  return PLUGIN_BRANDS.find((entry) => entry.tokens.some((token) => search.includes(token)))?.brand ?? DEFAULT_PLUGIN_BRAND
}

function primaryHero(view: PluginPageView) {
  return view.blocks.find((block): block is HeroBlock => block.type === "hero")
}

function setupConnectionTone(status: string): PluginConnectionTone {
  if (status === "connected" || status === "verified") return "connected"
  if (status === "verifying") return "checking"
  if (status === "warning") return "warning"
  if (status === "error" || status === "missing" || status === "needs_credentials") return "disconnected"
  return "idle"
}

function connectionStateFromText(value: string): PluginConnectionState | undefined {
  const text = value.trim().toLowerCase()
  if (!text) return undefined
  if (/\b(disconnected|not connected|missing|needs credentials)\b/.test(text)) return { tone: "disconnected", label: "Disconnected" }
  if (/\b(connected|verified|synced)\b/.test(text)) return { tone: "connected", label: "Connected" }
  if (/\b(verifying|checking|refreshing)\b/.test(text)) return { tone: "checking", label: "Checking" }
  if (/\b(warning|limited|attention)\b/.test(text)) return { tone: "warning", label: "Attention" }
  return undefined
}

function connectionStateFromView(view: PluginPageView): PluginConnectionState | undefined {
  if (view.setup?.status) {
    const tone = setupConnectionTone(view.setup.status)
    if (tone === "connected") return { tone, label: "Connected" }
    if (tone === "disconnected") return { tone, label: "Disconnected" }
    if (tone === "checking") return { tone, label: "Checking" }
    if (tone === "warning") return { tone, label: "Attention" }
  }
  const hero = primaryHero(view)
  return (
    connectionStateFromText(hero?.eyebrow ?? "") ??
    connectionStateFromText(view.description ?? "") ??
    (view.description?.trim().toLowerCase().startsWith("connect ") ? { tone: "disconnected", label: "Disconnected" } : undefined)
  )
}

function connectionBadgeClass(tone: PluginConnectionTone) {
  if (tone === "connected") return "border-[#16a34a]/35 bg-[#dcfce7] text-[#166534]"
  if (tone === "disconnected") return "border-[#dc2626]/35 bg-[#fee2e2] text-[#991b1b]"
  if (tone === "checking") return "border-[#2563eb]/30 bg-[#dbeafe] text-[#1e40af]"
  if (tone === "warning") return "border-[#d97706]/35 bg-[#fef3c7] text-[#92400e]"
  return "border-border-weak-base bg-background-secondary text-text-base"
}

function pluginShellTitle(view: PluginPageView) {
  const state = connectionStateFromView(view)
  const hero = primaryHero(view)
  if (state && hero?.title) return hero.title
  return view.title
}

function ConnectionStatusBadge(props: { state: PluginConnectionState }) {
  return (
    <span
      data-plugin-connection-status={props.state.tone}
      class={`shrink-0 rounded-md border px-2.5 py-1 text-11-medium ${connectionBadgeClass(props.state.tone)}`}
    >
      {props.state.label}
    </span>
  )
}

function heroEyebrowClass(value: string | undefined) {
  const state = connectionStateFromText(value ?? "")
  if (state) return connectionBadgeClass(state.tone)
  return "border-border-weak-base bg-background-secondary text-text-base"
}

function primaryHeroProvidesShellTitle(view: PluginPageView, hero: HeroBlock) {
  return Boolean(connectionStateFromView(view) && primaryHero(view)?.id === hero.id)
}

function PluginShellHeader(props: {
  view: PluginPageView
  state: PluginViewState
  readonly?: boolean
  onAction: (request: PluginActionRequest) => void | Promise<void>
  onSubmit: (blockId: string, values: Record<string, unknown>, confirmed?: boolean) => void | Promise<void>
  onLayoutLockChange?: (locked: boolean) => void
}) {
  const references = createMemo(() => mergedDashboardReferences(props.view.dashboard?.componentIndex))
  const importantReferences = createMemo(() =>
    references().filter((reference) => reference.importance === "critical" || reference.importance === "high").slice(0, 6),
  )
  const locked = createMemo(() => Boolean(props.view.dashboard?.layout?.locked))
  const dashboardTitle = createMemo(() => props.view.dashboard?.title ?? "Live dashboard")
  const setupEnabled = createMemo(() => Boolean(props.view.setup))
  const dashboardDisplay = createMemo(() => props.view.dashboard?.display ?? "auto")
  const dashboardAvailable = createMemo(() => Boolean(props.view.dashboard || props.view.blocks.some(dashboardCapableBlock)))
  const dashboardControlsEnabled = createMemo(() => dashboardDisplay() !== "hidden" && dashboardAvailable())
  const dashboardPanelEnabled = createMemo(() => dashboardDisplay() === "auto" && dashboardAvailable())
  const brand = createMemo(() => pluginBrand(props.view))
  const connectionState = createMemo(() => connectionStateFromView(props.view))
  const shellTitle = createMemo(() => pluginShellTitle(props.view))

  return (
    <div class="flex flex-col gap-4">
      <header
        data-plugin-shell-header
        data-plugin-accent={brand().accent}
        class="relative overflow-hidden rounded-lg border border-border-weak-base/80 bg-background-base/95 p-5 shadow-xs"
        style={{
          "border-color": brand().tint,
          background: `linear-gradient(135deg, ${brand().wash}, rgba(255,255,255,0) 58%), var(--background-base)`,
        }}
      >
        <div
          aria-hidden="true"
          class="absolute inset-x-0 top-0 h-1"
          style={{ background: `linear-gradient(90deg, ${brand().accent}, ${brand().tint})` }}
        />
        <div class="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
          <div class="min-w-0 flex-1">
            <div class="flex flex-wrap items-center gap-2">
              <span
                aria-hidden="true"
                class="size-2 rounded-full"
                style={{ "background-color": brand().accent, "box-shadow": `0 0 0 4px ${brand().tint}` }}
              />
              <div class="text-12-medium uppercase tracking-[0.18em]" style={{ color: brand().ink }}>
                Workspace Plugin
              </div>
              <Show when={connectionState()} keyed>
                {(state) => <ConnectionStatusBadge state={state} />}
              </Show>
            </div>
            <h1
              data-plugin-shell-title
              class="mt-3 max-w-full truncate font-display italic text-[34px] leading-[1.05] sm:text-[40px]"
              style={{ color: brand().accent }}
            >
              {shellTitle()}
            </h1>
            <Show when={props.view.description}>
              <p class="mt-2 max-w-3xl text-14-regular text-text-base">{props.view.description}</p>
            </Show>
          </div>
        <Show when={dashboardControlsEnabled()}>
          <div class="flex flex-wrap gap-2">
            <Button
              type="button"
              size="small"
              variant="secondary"
              icon="document"
              title="Download dashboard report"
              onClick={() =>
                downloadPluginFile(`${safeFilename(props.view.title)}-report.md`, "text/markdown", dashboardReport(props.view))
              }
            >
              Report
            </Button>
            <Button
              type="button"
              size="small"
              variant="secondary"
              icon="download"
              title="Download dashboard manifest"
              onClick={() =>
                downloadPluginFile(
                  `${safeFilename(props.view.title)}-dashboard.json`,
                  "application/json",
                  JSON.stringify(redactPluginPageView(props.view), null, 2),
                )
              }
            >
              Dashboard
            </Button>
            <Button
              type="button"
              size="small"
              variant={locked() ? "primary" : "secondary"}
              icon={locked() ? "edit" : "shield"}
              title={locked() ? "Unlock dashboard layout" : "Lock dashboard layout"}
              disabled={props.readonly || !props.onLayoutLockChange}
              onClick={() => props.onLayoutLockChange?.(!locked())}
            >
              {locked() ? "Unlock layout" : "Lock layout"}
            </Button>
          </div>
        </Show>
        </div>
      </header>

      <Show when={setupEnabled() || dashboardPanelEnabled()}>
        <div
          class="grid grid-cols-1 gap-3"
          classList={{ "xl:grid-cols-[minmax(0,1.1fr)_minmax(340px,0.9fr)]": setupEnabled() && dashboardPanelEnabled() }}
        >
          <Show when={setupEnabled()}>
            <PluginSetupPanel
              view={props.view}
              setup={props.view.setup}
              state={props.state}
              readonly={props.readonly}
              onAction={props.onAction}
              onSubmit={props.onSubmit}
            />
          </Show>
          <Show when={dashboardPanelEnabled()}>
            <section class="min-w-0 rounded-lg border border-border-weak-base/80 bg-background-base/95 p-4 shadow-xs">
              <div class="flex items-start justify-between gap-3">
                <div class="min-w-0">
                  <div class="text-12-medium uppercase tracking-[0.14em] text-text-base">{dashboardTitle()}</div>
                  <Show when={props.view.dashboard?.description}>
                    <p class="mt-1 text-13-regular text-text-base">{props.view.dashboard?.description}</p>
                  </Show>
                </div>
                <span
                  class="shrink-0 rounded-full border px-2 py-1 text-11-medium"
                  classList={{
                    "border-border-weak-base bg-background-secondary text-text-base": !locked(),
                    "border-border-strong bg-background-secondary text-text-strong": locked(),
                  }}
                >
                  {locked() ? "Locked" : "Editable"}
                </span>
              </div>
              <Show when={locked()}>
                <p class="mt-3 rounded-md border border-border-weak-base bg-background-secondary px-3 py-2 text-12-regular text-text-base">
                  {props.view.dashboard?.layout?.lockedReason ??
                    "Dashboard layout is locked. Chat should ask before changing structure, block order, or chart layout."}
                </p>
              </Show>
              <div class="mt-4 grid grid-cols-2 gap-2 text-12-regular text-text-base sm:grid-cols-4">
                <MetricPill label="Blocks" value={String(props.view.blocks.length)} />
                <MetricPill label="Charts" value={String(props.view.blocks.filter((block) => block.type === "chart").length)} />
                <MetricPill label="Actions" value={String(countDashboardActions(props.view))} />
                <MetricPill label="Refs" value={String(references().length)} />
              </div>
              <Show when={importantReferences().length}>
                <div class="mt-4 flex flex-wrap gap-2">
                  <For each={importantReferences()}>
                    {(reference) => (
                      <span
                        title={reference.description}
                        class="rounded-md border border-border-weak-base bg-background-secondary px-2 py-1 text-11-medium text-text-base"
                      >
                        {reference.label}
                      </span>
                    )}
                  </For>
                </div>
              </Show>
              <Show when={(props.view.dashboard?.actions?.length ?? 0) > 0 && !props.readonly}>
                <div class="mt-4">
                  <TopLevelActions
                    blockId={DASHBOARD_BLOCK_ID}
                    view={props.view}
                    actions={props.view.dashboard?.actions}
                    state={props.state}
                    onAction={props.onAction}
                  />
                </div>
              </Show>
            </section>
          </Show>
        </div>
      </Show>
    </div>
  )
}

export function PluginSetupPanel(props: {
  view: PluginPageView
  setup?: PluginPageSetup
  state: PluginViewState
  readonly?: boolean
  onAction: (request: PluginActionRequest) => void | Promise<void>
  onSubmit: (blockId: string, values: Record<string, unknown>, confirmed?: boolean) => void | Promise<void>
}) {
  const status = createMemo(() => props.setup?.status ?? "idle")
  const credentials = createMemo(() => props.setup?.credentials ?? [])
  const connections = createMemo(() => props.setup?.connections ?? [])
  return (
    <section
      data-plugin-setup-panel=""
      class="min-w-0 rounded-lg border border-border-weak-base/80 bg-background-base/95 p-4 shadow-xs"
    >
      <div class="flex flex-col gap-3">
        <div class="flex items-start justify-between gap-3">
          <div class="min-w-0">
            <div class="text-12-medium uppercase tracking-[0.14em] text-text-base">
              {props.setup?.title ?? "Connection setup"}
            </div>
            <p class="mt-1 text-13-regular text-text-base">
              {props.setup?.description ??
                "Credentials, verification, and connection health for this plugin live here when the plugin declares them."}
            </p>
          </div>
          <span class={`shrink-0 rounded-md border px-2.5 py-1 text-11-medium ${statusClass(status())}`}>
            {setupStatusLabel(status())}
          </span>
        </div>

        <Show when={props.setup?.provider}>
          <div class="rounded-md border border-border-weak-base bg-background-secondary px-3 py-2 text-12-regular text-text-base">
            Provider: <span class="text-text-strong">{props.setup?.provider}</span>
          </div>
        </Show>

        <Show when={credentials().length}>
          <div class="grid grid-cols-1 gap-2 sm:grid-cols-2">
            <For each={credentials()}>
              {(credential) => (
                <CredentialPill credential={credential} />
              )}
            </For>
          </div>
        </Show>

        <Show when={connections().length}>
          <div class="grid grid-cols-1 gap-2 sm:grid-cols-2">
            <For each={connections()}>
              {(connection) => (
                <div class="rounded-md border border-border-weak-base bg-background-secondary p-3">
                  <div class="flex items-center justify-between gap-2">
                    <div class="min-w-0">
                      <span class="block truncate text-13-medium text-text-strong">{connection.label}</span>
                      <Show when={connection.active}>
                        <span class="text-11-medium text-[#ea580c]">Active</span>
                      </Show>
                    </div>
                    <div class="flex shrink-0 items-center gap-1.5">
                      <Show when={connection.active}>
                        <Icon name="check" class="size-4 text-[#ea580c]" />
                      </Show>
                      <span class={`rounded-md border px-2 py-0.5 text-11-medium ${statusClass(connection.status)}`}>
                        {setupStatusLabel(connection.status)}
                      </span>
                    </div>
                  </div>
                  <Show when={connection.description}>
                    <p class="mt-1 text-12-regular text-text-base">{connection.description}</p>
                  </Show>
                  <Show when={connection.lastVerifiedAt}>
                    <p class="mt-1 text-11-regular text-text-weak">Verified {connection.lastVerifiedAt}</p>
                  </Show>
                  <Show when={connection.actions?.length && !props.readonly}>
                    <div class="mt-3">
                      <TopLevelActions
                        blockId={SETUP_BLOCK_ID}
                        view={props.view}
                        actions={connection.actions}
                        state={props.state}
                        onAction={props.onAction}
                      />
                    </div>
                  </Show>
                </div>
              )}
            </For>
          </div>
        </Show>

        <Show when={props.setup?.actions?.length && !props.readonly}>
          <TopLevelActions
            blockId={SETUP_BLOCK_ID}
            view={props.view}
            actions={props.setup?.actions}
            state={props.state}
            onAction={props.onAction}
          />
        </Show>

        <Show when={props.readonly ? undefined : props.setup?.form}>
          {(form) => (
            <FormWidget
              fields={form().fields ?? []}
              sections={form().sections}
              data={{ values: form().values, note: form().note }}
              state={{
                submitState: {
                  pending: props.state.pendingSubmitIds.has(SETUP_BLOCK_ID),
                  ...(props.state.submitState?.[SETUP_BLOCK_ID] ?? {}),
                },
              }}
              submitLabel={form().submitLabel ?? "Verify connection"}
              submitAction={form().submitAction}
              onSubmit={(values) => {
                const submitAction = form().submitAction
                if (submitAction && !confirmAction({ ...submitAction, toolName: form().submitToolName })) return
                props.onSubmit(SETUP_BLOCK_ID, values, submitAction ? actionRequiresConfirmation(submitAction) : undefined)
              }}
            />
          )}
        </Show>
      </div>
    </section>
  )
}

function CredentialPill(props: { credential: PluginSetupCredential }) {
  const state = () => {
    if (props.credential.verified) return "verified"
    if (props.credential.configured) return "configured"
    if (props.credential.required) return "required"
    return "optional"
  }
  return (
    <div class="rounded-md border border-border-weak-base bg-background-secondary p-3">
      <div class="flex items-center justify-between gap-2">
        <span class="text-13-medium text-text-strong">{props.credential.label}</span>
        <span class="rounded-full border border-border-weak-base bg-background-base px-2 py-0.5 text-11-medium text-text-base">
          {state()}
        </span>
      </div>
      <Show when={props.credential.description}>
        <p class="mt-1 text-12-regular text-text-base">{props.credential.description}</p>
      </Show>
    </div>
  )
}

function MetricPill(props: { label: string; value: string }) {
  return (
    <div class="rounded-md border border-border-weak-base bg-background-secondary px-3 py-2">
      <div class="text-11-medium uppercase tracking-[0.12em] text-text-weak">{props.label}</div>
      <div class="mt-1 text-15-medium text-text-strong">{props.value}</div>
    </div>
  )
}

function TopLevelActions(props: {
  blockId: string
  view: PluginPageView
  actions?: WidgetActionDeclaration[]
  state: PluginViewState
  onAction: (request: PluginActionRequest) => void | Promise<void>
}) {
  const context = () => ({
    view: props.view,
    block: { id: props.blockId, type: props.blockId },
    values: {},
    actionState: props.state.actionState ?? {},
    submitState: props.state.submitState ?? {},
  })
  const visible = () => (props.actions ?? []).filter((action) => actionVisible(action, context()))
  return (
    <div class="flex flex-wrap gap-2">
      <For each={visible()}>
        {(action) => {
          const key = pluginActionStateKey({ blockId: props.blockId, actionId: action.id })
          const disabled = () => actionDisabled(action, context())
          return (
            <Button
              size="small"
              variant={action.variant ?? "secondary"}
              disabled={disabled() || props.state.pendingActionIds.has(key)}
              title={disabled() ? (action.disabledReason ?? "Action unavailable") : undefined}
              onClick={() => {
                if (disabled() || !confirmAction(action)) return
                props.onAction({
                  blockId: props.blockId,
                  actionId: action.id,
                  confirmed: actionRequiresConfirmation(action),
                })
              }}
            >
              {props.state.pendingActionIds.has(key) ? `${action.label}...` : action.label}
            </Button>
          )
        }}
      </For>
    </div>
  )
}

function setupStatusLabel(status: string) {
  if (status === "needs_credentials") return "Needs credentials"
  return status
    .split("_")
    .filter(Boolean)
    .map((part) => `${part.charAt(0).toUpperCase()}${part.slice(1)}`)
    .join(" ")
}

function statusClass(status: string) {
  return connectionBadgeClass(setupConnectionTone(status))
}

function countDashboardActions(view: PluginPageView) {
  return (
    (view.setup?.actions?.length ?? 0) +
    (view.dashboard?.actions?.length ?? 0) +
    view.blocks.reduce((count, block) => count + blockActionCount(block), 0)
  )
}

function dashboardCapableBlock(block: PluginBlock): boolean {
  return (
    block.type === "stats" ||
    block.type === "calendar" ||
    block.type === "chart" ||
    block.type === "table" ||
    block.type === "todo"
  )
}

function blockActionCount(block: PluginBlock): number {
  const own =
    block.type === "stats" ||
    block.type === "list" ||
    block.type === "detail" ||
    block.type === "calendar" ||
    block.type === "card_grid" ||
    block.type === "notice" ||
    block.type === "registry"
      ? (block.actions?.length ?? 0)
      : 0
  const row =
    block.type === "table"
      ? (block.rowActions?.length ?? 0)
      : block.type === "list" || block.type === "card_grid"
        ? (block.itemActions?.length ?? 0)
        : 0
  const nested = block.type === "table" ? Object.values(block.expandedRows ?? {}).flat().reduce((sum, item) => sum + blockActionCount(item), 0) : 0
  return own + row + nested
}

function safeFilename(value: string) {
  return value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9._-]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 80) || "plugin-dashboard"
}

function downloadPluginFile(filename: string, type: string, content: string) {
  const url = URL.createObjectURL(new Blob([content], { type }))
  const link = document.createElement("a")
  link.href = url
  link.download = filename
  link.click()
  window.setTimeout(() => URL.revokeObjectURL(url), 0)
}

function dashboardReport(view: PluginPageView) {
  const references = mergedDashboardReferences(view.dashboard?.componentIndex)
  return [
    `# ${view.title}`,
    view.description ?? "",
    "",
    "## Setup",
    `Status: ${setupStatusLabel(view.setup?.status ?? "idle")}`,
    view.setup?.provider ? `Provider: ${view.setup.provider}` : undefined,
    "",
    "## Dashboard",
    `Layout: ${view.dashboard?.layout?.locked ? "Locked" : "Editable"}`,
    `Blocks: ${view.blocks.length}`,
    `Actions: ${countDashboardActions(view)}`,
    "",
    "## Blocks",
    ...view.blocks.map((block) => `- ${block.id}: ${block.type}${"title" in block && block.title ? ` - ${block.title}` : ""}`),
    "",
    "## Component References",
    ...references.map((reference) => `- ${reference.label} (${reference.kind})${reference.path ? `: ${reference.path}` : ""}`),
    "",
  ]
    .filter((line): line is string => line !== undefined)
    .join("\n")
}

function createBlockSlot(block: PluginBlock) {
  const [value, setValue] = createSignal(block, { equals: false })
  return { id: block.id, value, setValue }
}

function stableBlocks(blocks: PluginBlock[], slots: Map<string, ReturnType<typeof createBlockSlot>>) {
  const active = new Set(blocks.map((block) => block.id))
  slots.forEach((_, id) => {
    if (!active.has(id)) slots.delete(id)
  })
  return blocks.map((block) => {
    const current = slots.get(block.id)
    if (current) {
      current.setValue(block)
      return current
    }
    const next = createBlockSlot(block)
    slots.set(block.id, next)
    return next
  })
}

function pluginBlockFull(block: PluginBlock) {
  if (block.type === "hero") return true
  if (block.type === "calendar") return true
  if (block.type === "list" && isAvailableToolsBlock(block)) return true
  if (block.type === "table") return block.columns.length >= 5
  if (block.type === "preview") return block.mode !== "markdown"
  if (block.type === "artifact_editor") return true
  if (block.type === "visual_pipeline") return true
  if (block.type === "registry") return block.span === "full"
  return false
}

function isAvailableToolsBlock(block: PluginBlock) {
  if (block.type !== "list") return false
  const title = block.title?.trim().toLowerCase()
  return block.id === "tools" || title === "available tool calls" || title === "available tools"
}

function PluginBlockFrame(props: {
  block: PluginBlock
  title?: string
  full?: boolean
  children: JSX.Element
}) {
  return (
    <section
      data-plugin-block={props.block.id}
      data-plugin-block-type={props.block.type}
      class="oa-block-enter group h-full min-w-0 overflow-hidden rounded-2xl border border-border-weak-base bg-background-base p-5 shadow-[0_1px_2px_rgba(15,23,42,0.04)] transition-all duration-200 hover:border-border-base hover:shadow-[0_14px_40px_-22px_rgba(15,23,42,0.28)]"
      classList={{
        "xl:col-span-2": Boolean(props.full),
      }}
    >
      <Show when={props.title}>
        <div class="mb-4 flex items-center gap-2.5">
          <span
            aria-hidden="true"
            class="size-2 shrink-0 rounded-full"
            style={{ "background-color": "var(--oa-plugin-accent, #6366f1)" }}
          />
          <h2 class="text-[15px] font-semibold leading-6 text-text-strong">{props.title}</h2>
        </div>
      </Show>
      {props.children}
    </section>
  )
}

function actionContext(input: {
  view?: PluginPageView
  block: PluginBlock
  row?: Record<string, unknown>
  values?: Record<string, unknown>
  state: PluginViewState
}) {
  return {
    view: input.view,
    block: input.block,
    row: input.row,
    values: input.values ?? {},
    actionState: input.state.actionState ?? {},
    submitState: input.state.submitState ?? {},
  }
}

function actionVisible(
  action: WidgetActionDeclaration | Omit<WidgetActionDeclaration, "toolName">,
  context: Record<string, unknown>,
) {
  if (action.visibleWhen === undefined) return true
  return pluginConditionAllowed(action.visibleWhen) && evaluatePluginCondition(action.visibleWhen, context)
}

function actionDisabled(
  action: WidgetActionDeclaration | Omit<WidgetActionDeclaration, "toolName">,
  context: Record<string, unknown>,
) {
  if (action.disabledWhen === undefined) return false
  return !pluginConditionAllowed(action.disabledWhen) || evaluatePluginCondition(action.disabledWhen, context)
}

function fieldVisible(field: WidgetFieldDeclaration, context: Record<string, unknown>) {
  if (field.visibleWhen === undefined) return true
  return pluginConditionAllowed(field.visibleWhen) && evaluatePluginCondition(field.visibleWhen, context)
}

function actionRequiresConfirmation(action: WidgetActionDeclaration | Omit<WidgetActionDeclaration, "toolName">) {
  return Boolean(action.confirm) || action.risk === "external_write" || action.risk === "destructive"
}

function confirmAction(action: WidgetActionDeclaration | Omit<WidgetActionDeclaration, "toolName">) {
  if (!actionRequiresConfirmation(action)) return true
  return window.confirm(
    [
      action.confirm?.title ?? action.label,
      action.confirm?.description,
      action.confirm?.sideEffect,
      action.confirm?.reversibility,
      action.confirm?.payloadSummary,
    ]
      .filter((item): item is string => Boolean(item))
      .join("\n\n"),
  )
}

function renderBlockActions(input: {
  view: PluginPageView
  block: PluginBlock
  actions?: WidgetActionDeclaration[]
  state: PluginViewState
  onAction: (request: PluginActionRequest) => void | Promise<void>
}) {
  const context = () => actionContext({ view: input.view, block: input.block, state: input.state })
  const visible = () => (input.actions ?? []).filter((action) => actionVisible(action, context()))
  return (
    <Show when={visible().length}>
      <div class="mb-4 flex flex-wrap gap-2">
        <For each={visible()}>
          {(action) => {
            const key = pluginActionStateKey({ blockId: input.block.id, actionId: action.id })
            const disabled = () => actionDisabled(action, context())
            return (
              <Button
                size="small"
                variant={action.variant ?? "secondary"}
                disabled={disabled() || input.state.pendingActionIds.has(key)}
                title={disabled() ? (action.disabledReason ?? "Action unavailable") : undefined}
                onClick={() => {
                  if (disabled() || !confirmAction(action)) return
                  input.onAction({
                    blockId: input.block.id,
                    actionId: action.id,
                    confirmed: actionRequiresConfirmation(action),
                  })
                }}
              >
                {input.state.pendingActionIds.has(key) ? `${action.label}...` : action.label}
              </Button>
            )
          }}
        </For>
      </div>
    </Show>
  )
}

const CALENDAR_LANE_LIMIT = 3
const DAY_MS = 24 * 60 * 60 * 1000
const CALENDAR_STATUS_COLORS = [
  { color: "#1f6b43", background: "#dcf8e4" },
  { color: "#a24b2b", background: "#fee2d4" },
  { color: "#225d9f", background: "#dfeeff" },
  { color: "#7350a9", background: "#eee5ff" },
  { color: "#5a4c40", background: "#eee7dd" },
  { color: "#8d3029", background: "#fde0dc" },
]

function calendarDate(value: string) {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value)
  if (!match) return
  const date = new Date(Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3])))
  if (date.toISOString().slice(0, 10) !== value) return
  return date
}

function calendarMonthStart(value: string) {
  const match = /^(\d{4})-(\d{2})$/.exec(value)
  if (match) {
    const date = new Date(Date.UTC(Number(match[1]), Number(match[2]) - 1, 1))
    if (date.toISOString().slice(0, 7) === value) return date
  }
  const now = new Date()
  return new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 1))
}

function addCalendarDays(date: Date, days: number) {
  return new Date(date.valueOf() + days * DAY_MS)
}

function calendarDateID(date: Date) {
  return date.toISOString().slice(0, 10)
}

function calendarDayDiff(start: Date, end: Date) {
  return Math.round((end.valueOf() - start.valueOf()) / DAY_MS)
}

function calendarWeeks(visibleMonth: string) {
  const monthStart = calendarMonthStart(visibleMonth)
  const weekStartOffset = (monthStart.getUTCDay() + 6) % 7
  const gridStart = addCalendarDays(monthStart, -weekStartOffset)
  return Array.from({ length: 6 }, (_, weekIndex) =>
    Array.from({ length: 7 }, (_, dayIndex) => addCalendarDays(gridStart, weekIndex * 7 + dayIndex)),
  )
}

function calendarMonthLabel(visibleMonth: string) {
  return new Intl.DateTimeFormat("en", { month: "long", year: "numeric", timeZone: "UTC" }).format(
    calendarMonthStart(visibleMonth),
  )
}

function calendarStatusStyle(block: CalendarBlock, item: CalendarItem) {
  const explicit = block.statuses?.find((status) => status.id === item.status)
  if (explicit?.color || explicit?.background) {
    return {
      color: explicit.color ?? "#2f5f9d",
      background: explicit.background ?? "#dcecff",
    }
  }
  const index = Math.abs(String(item.status ?? item.channel ?? item.id).split("").reduce((sum, char) => sum + char.charCodeAt(0), 0))
  return CALENDAR_STATUS_COLORS[index % CALENDAR_STATUS_COLORS.length]
}

function calendarChannelColor(block: CalendarBlock, item: CalendarItem) {
  return block.channels?.find((channel) => channel.id === item.channel)?.color ?? calendarStatusStyle(block, item).color
}

function calendarStatusLabel(block: CalendarBlock, item: CalendarItem) {
  return block.statuses?.find((status) => status.id === item.status)?.label ?? item.status ?? "Unstatused"
}

function calendarChannelLabel(block: CalendarBlock, item: CalendarItem) {
  return block.channels?.find((channel) => channel.id === item.channel)?.label ?? item.channel ?? ""
}

function calendarItemRow(item: CalendarItem): Record<string, unknown> {
  return { ...item, ...(item.meta ?? {}) }
}

function calendarItemValues(item: CalendarItem): Record<string, unknown> {
  return {
    itemId: item.id,
    title: item.title,
    startDate: item.startDate,
    endDate: item.endDate,
    status: item.status ?? "",
    channel: item.channel ?? "",
    ...(item.meta ?? {}),
  }
}

function calendarMetaEntries(item: CalendarItem) {
  return Object.entries(item.meta ?? {}).filter((entry) => entry[1] !== undefined && String(entry[1]).trim() !== "")
}

function calendarMetaLabel(key: string) {
  return key
    .replace(/[_-]+/g, " ")
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/\b\w/g, (char) => char.toUpperCase())
}

function calendarWeekSegments(block: CalendarBlock, week: Date[]) {
  const weekStart = calendarDateID(week[0])
  const weekEnd = calendarDateID(week[6])
  const candidates = block.items
    .flatMap((item) => {
      const startDate = calendarDate(item.startDate)
      const endDate = calendarDate(item.endDate)
      if (!startDate || !endDate || endDate < startDate || item.startDate > weekEnd || item.endDate < weekStart) return []
      const startIndex = Math.max(0, calendarDayDiff(week[0], startDate))
      const endIndex = Math.min(6, calendarDayDiff(week[0], endDate))
      return [{ item, startIndex, span: endIndex - startIndex + 1 }]
    })
    .sort((a, b) => a.startIndex - b.startIndex || b.span - a.span || a.item.title.localeCompare(b.item.title))
  const laneEnds: number[] = []
  const visible: Array<{ item: CalendarItem; startIndex: number; span: number; lane: number }> = []
  const hidden: Array<{ item: CalendarItem; startIndex: number; span: number; lane: number }> = []
  candidates.forEach((candidate) => {
    const existingLane = laneEnds.findIndex((endIndex) => endIndex < candidate.startIndex)
    const lane = existingLane === -1 ? laneEnds.length : existingLane
    if (lane >= CALENDAR_LANE_LIMIT) {
      hidden.push({ ...candidate, lane })
      return
    }
    laneEnds[lane] = candidate.startIndex + candidate.span - 1
    visible.push({ ...candidate, lane })
  })
  return { visible, hidden }
}

function CalendarBlockView(props: {
  view: PluginPageView
  block: CalendarBlock
  state: PluginViewState
  readonly?: boolean
  onAction: (request: PluginActionRequest) => void | Promise<void>
  onSubmit: (blockId: string, values: Record<string, unknown>, confirmed?: boolean) => void | Promise<void>
}) {
  const [selectedID, setSelectedID] = createSignal<string>()
  const [visibleMonth, setVisibleMonth] = createSignal(props.block.visibleMonth)
  const weeks = createMemo(() => calendarWeeks(visibleMonth()))
  const monthID = createMemo(() => calendarMonthStart(visibleMonth()).toISOString().slice(0, 7))
  const visibleMonthRange = createMemo(() => {
    const start = calendarMonthStart(visibleMonth())
    return {
      start: calendarDateID(start),
      end: calendarDateID(new Date(Date.UTC(start.getUTCFullYear(), start.getUTCMonth() + 1, 0))),
    }
  })
  const today = new Date().toISOString().slice(0, 10)
  const visibleMonthItems = createMemo(() =>
    props.block.items.filter((item) => {
      const itemStart = calendarDate(item.startDate)
      const itemEnd = calendarDate(item.endDate)
      const range = visibleMonthRange()
      if (!itemStart || !itemEnd || itemEnd < itemStart) return false
      return item.startDate <= range.end && item.endDate >= range.start
    }),
  )
  const selectableItems = createMemo(() =>
    [...visibleMonthItems()].sort(
      (a, b) => a.startDate.localeCompare(b.startDate) || a.endDate.localeCompare(b.endDate) || a.title.localeCompare(b.title),
    ),
  )
  const selectedItem = createMemo(() => visibleMonthItems().find((item) => item.id === selectedID()))
  const selectedRow = createMemo(() => {
    const item = selectedItem()
    return item ? calendarItemRow(item) : undefined
  })
  const emptyMonth = createMemo(() => selectableItems().length === 0)
  const emptyMonthTitle = createMemo(() =>
    props.block.items.length === 0
      ? (props.block.emptyState?.title ?? "No calendar items yet")
      : `No planned items in ${calendarMonthLabel(visibleMonth())}`,
  )
  const emptyMonthDescription = createMemo(() =>
    props.block.items.length === 0
      ? props.block.emptyState?.description
      : undefined,
  )
  const visibleActions = createMemo(() => {
    const row = selectedRow()
    if (!row || props.readonly) return []
    return (props.block.actions ?? []).filter((action) =>
      actionVisible(action, actionContext({ view: props.view, block: props.block, row, state: props.state })),
    )
  })

  createEffect(() => {
    setVisibleMonth(props.block.visibleMonth)
  })

  createEffect(() => {
    if (selectedID() && visibleMonthItems().some((item) => item.id === selectedID())) return
    setSelectedID(visibleMonthItems()[0]?.id)
  })

  const shiftVisibleMonth = (delta: number) => {
    const current = calendarMonthStart(visibleMonth())
    setVisibleMonth(new Date(Date.UTC(current.getUTCFullYear(), current.getUTCMonth() + delta, 1)).toISOString().slice(0, 7))
  }

  return (
    <div
      data-calendar-block={props.block.id}
      class="relative -m-4 flex flex-col gap-5 overflow-hidden rounded-lg bg-[#f7f2ea] p-4 text-[#25211d]"
    >
      <div class="pointer-events-none absolute inset-x-0 top-0 h-32 bg-[linear-gradient(135deg,rgba(225,124,86,0.2),rgba(255,250,243,0.94)_46%,rgba(62,130,190,0.13)_100%)]" />
      <div class="relative flex flex-col gap-4 rounded-xl border border-[#e4d8cb] bg-[#fff8ef]/80 p-4 shadow-[0_1px_0_rgba(255,255,255,0.85),0_16px_38px_rgba(72,53,35,0.07)] lg:flex-row lg:items-start lg:justify-between">
        <div class="min-w-0">
          <div class="text-11-medium uppercase text-[#7c7067]">{props.block.title ?? "Calendar"}</div>
          <div class="mt-1 text-28-semibold text-[#211d19]">{calendarMonthLabel(visibleMonth())}</div>
          <div class="mt-3 inline-flex rounded-full border border-[#e5d8cb] bg-[#fffdf8] px-3 py-1.5 text-12-medium text-[#5f534a] shadow-[0_1px_0_rgba(255,255,255,0.9)]">
            {selectableItems().length} {selectableItems().length === 1 ? "item" : "items"} planned this month
          </div>
        </div>
        <div class="flex flex-col items-start gap-3 lg:items-end">
          <div class="inline-flex items-center gap-1 rounded-full border border-[#e0d4c9] bg-[#fffdf8]/85 p-1 shadow-[0_1px_0_rgba(255,255,255,0.85)]">
            <button
              type="button"
              data-calendar-month-previous
              aria-label="Previous month"
              title="Previous month"
              class="inline-flex h-8 w-8 items-center justify-center rounded-full text-[#675a51] transition hover:bg-[#f2e5da] hover:text-[#211d19]"
              onClick={() => shiftVisibleMonth(-1)}
            >
              <Icon name="chevron-left" size="small" />
            </button>
            <button
              type="button"
              data-calendar-month-today
              class="h-8 rounded-full px-3 text-12-medium text-[#5e5148] transition hover:bg-[#f2e5da] hover:text-[#211d19]"
              onClick={() => setVisibleMonth(new Date().toISOString().slice(0, 7))}
            >
              Today
            </button>
            <button
              type="button"
              data-calendar-month-next
              aria-label="Next month"
              title="Next month"
              class="inline-flex h-8 w-8 items-center justify-center rounded-full text-[#675a51] transition hover:bg-[#f2e5da] hover:text-[#211d19]"
              onClick={() => shiftVisibleMonth(1)}
            >
              <Icon name="chevron-right" size="small" />
            </button>
          </div>
          <Show when={(props.block.statuses?.length ?? 0) > 0}>
            <div class="flex max-w-3xl flex-wrap justify-start gap-2 lg:justify-end">
              <For each={props.block.statuses}>
                {(status) => (
                  <span
                    data-calendar-status-chip={status.id}
                    class="inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-12-medium text-[#4f463f] shadow-[0_1px_0_rgba(255,255,255,0.7)]"
                    style={{ "border-color": status.color ?? "#d9cfc4", "background-color": status.background ?? "#fffaf3" }}
                  >
                    <span class="h-1.5 w-1.5 rounded-full" style={{ "background-color": status.color ?? "#8b8177" }} />
                    {status.label}
                  </span>
                )}
              </For>
            </div>
          </Show>
        </div>
      </div>

      <Show when={(props.block.diagnostics?.length ?? 0) > 0}>
        <div class="relative flex flex-col gap-2">
          <For each={props.block.diagnostics}>
            {(diagnostic) => (
              <div
                class="rounded-md border border-[#e2d6c9] bg-[#fffaf3] px-3 py-2 text-12-regular shadow-[0_1px_0_rgba(255,255,255,0.7)]"
                classList={{
                  "text-text-danger": diagnostic.tone === "error",
                  "text-text-warning": diagnostic.tone === "warning",
                  "text-text-success": diagnostic.tone === "success",
                  "text-text-base": !diagnostic.tone || diagnostic.tone === "info",
                }}
              >
                {diagnostic.message}
              </div>
            )}
          </For>
        </div>
      </Show>

      <div class="relative grid grid-cols-1 gap-5 2xl:grid-cols-[minmax(0,1fr)_400px]">
        <div class="relative min-w-0 overflow-hidden rounded-xl border border-[#dfd3c6] bg-[#fffbf5] shadow-[0_1px_0_rgba(255,255,255,0.9),0_14px_34px_rgba(72,53,35,0.08)]">
          <div class="grid grid-cols-7 border-b border-[#e8ded3] bg-[#fbf6ef] text-center text-11-medium uppercase text-[#8a7d72]">
            <For each={["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"]}>{(day) => <div class="py-3">{day}</div>}</For>
          </div>
          <div class="flex flex-col">
            <For each={weeks()}>
              {(week) => {
                const segments = createMemo(() => calendarWeekSegments(props.block, week))
                return (
                  <div class="relative min-h-[8.75rem] border-b border-[#e8ded3] last:border-b-0">
                    <div class="grid h-full min-h-[8.75rem] grid-cols-7">
                      <For each={week}>
                        {(day) => {
                          const dateID = calendarDateID(day)
                          return (
                            <div
                              class="min-w-0 border-r border-[#ece3da] p-2.5 last:border-r-0"
                              classList={{
                                "bg-[#fffbf5]": dateID.slice(0, 7) === monthID(),
                                "bg-[#f6efe6] text-[#9c9085]": dateID.slice(0, 7) !== monthID(),
                                "bg-[#fff0e7] shadow-[inset_0_0_0_1px_rgba(225,124,86,0.42)]": dateID === today,
                              }}
                            >
                              <span
                                class="inline-flex h-6 min-w-6 items-center justify-center rounded-full px-1 text-12-medium"
                                classList={{
                                  "bg-[#211d19] text-[#fffaf3]": dateID === today,
                                  "text-[#403832]": dateID !== today && dateID.slice(0, 7) === monthID(),
                                  "text-[#9c9085]": dateID !== today && dateID.slice(0, 7) !== monthID(),
                                }}
                              >
                                {String(day.getUTCDate()).padStart(2, "0")}
                              </span>
                            </div>
                          )
                        }}
                      </For>
                    </div>
                    <div class="pointer-events-none absolute inset-x-0 top-11">
                      <For each={segments().visible}>
                        {(segment) => {
                          const style = calendarStatusStyle(props.block, segment.item)
                          const channelColor = calendarChannelColor(props.block, segment.item)
                          return (
                            <button
                              type="button"
                              data-calendar-item={segment.item.id}
                              title={`${segment.item.title} - ${segment.item.startDate} to ${segment.item.endDate}`}
                              class="pointer-events-auto absolute flex h-8 min-w-0 items-center gap-1.5 overflow-hidden rounded-md border-l-[3px] border-y-0 border-r-0 px-2 text-left text-12-medium shadow-[0_1px_1px_rgba(41,32,24,0.08)] transition hover:-translate-y-px hover:shadow-[0_8px_18px_rgba(41,32,24,0.12)]"
                              classList={{
                                "ring-2 ring-[#dd7b55]": selectedID() === segment.item.id,
                              }}
                              style={{
                                left: `calc(${(segment.startIndex / 7) * 100}% + 0.45rem)`,
                                width: `calc(${(segment.span / 7) * 100}% - 0.9rem)`,
                                top: `${segment.lane * 2.15}rem`,
                                color: style.color,
                                "border-color": style.color,
                                "background-color": style.background,
                              }}
                              onClick={() => setSelectedID(segment.item.id)}
                            >
                              <span class="h-2 w-2 shrink-0 rounded-full" style={{ "background-color": channelColor }} />
                              <span class="min-w-0 truncate">{segment.item.title}</span>
                              <Show when={calendarChannelLabel(props.block, segment.item)}>
                                <span class="ml-auto shrink-0 text-11-regular opacity-75">
                                  {calendarChannelLabel(props.block, segment.item)}
                                </span>
                              </Show>
                            </button>
                          )
                        }}
                      </For>
                      <Show when={segments().hidden.length > 0}>
                        <button
                          type="button"
                          data-calendar-overflow
                          class="pointer-events-auto absolute right-2 rounded-full border border-[#dfd3c6] bg-[#fffaf3] px-2 py-1 text-11-medium text-[#5e5148] shadow-xs transition hover:bg-[#fff0e7]"
                          style={{ top: `${CALENDAR_LANE_LIMIT * 2.15}rem` }}
                          title={segments().hidden.map((segment) => segment.item.title).join(", ")}
                          onClick={() => {
                            const next = segments().hidden[0]?.item.id
                            if (next) setSelectedID(next)
                          }}
                        >
                          +{segments().hidden.length} more
                        </button>
                      </Show>
                    </div>
                  </div>
                )
              }}
            </For>
          </div>
          <Show when={emptyMonth()}>
            <div
              data-calendar-empty-state
              class="pointer-events-none absolute left-6 right-6 top-1/2 z-10 -translate-y-1/2 rounded-xl border border-dashed border-[#d5c6b8] bg-[#fffdf8]/95 px-5 py-6 text-center shadow-[0_16px_42px_rgba(72,53,35,0.12)]"
            >
              <div class="text-14-medium text-[#5b4e45]">{emptyMonthTitle()}</div>
              <Show when={emptyMonthDescription()}>
                <div class="mt-1 text-12-regular text-[#8a7d72]">{emptyMonthDescription()}</div>
              </Show>
            </div>
          </Show>
        </div>

        <aside class="min-w-0 rounded-xl border border-[#dfd3c6] bg-[#fffaf3] p-4 shadow-[0_1px_0_rgba(255,255,255,0.9),0_14px_34px_rgba(72,53,35,0.07)]">
          <Show
            when={selectedItem()}
            fallback={
              <div class="rounded-md border border-dashed border-[#d9cfc4] bg-[#fff8ef] px-3 py-4 text-center text-12-regular text-[#8a7d72]">
                {props.block.emptyState?.description ?? "Select a calendar item to edit it."}
              </div>
            }
          >
            {(item) => (
              <div class="flex flex-col gap-4">
                <div class="min-w-0 rounded-lg border border-[#eadfd4] bg-[#fff8ef] p-3">
                  <div class="text-11-medium uppercase tracking-[0.14em] text-[#8a7d72]">Selected item</div>
                  <h3 class="mt-1 text-18-medium text-[#211d19]">{item().title}</h3>
                  <p class="mt-1 text-12-regular text-[#6f6259]">
                    {item().startDate} to {item().endDate}
                  </p>
                  <div class="mt-3 flex flex-wrap gap-2">
                    <span
                      class="rounded-full border px-2 py-1 text-11-medium"
                      style={{
                        color: calendarStatusStyle(props.block, item()).color,
                        "border-color": calendarStatusStyle(props.block, item()).color,
                        "background-color": calendarStatusStyle(props.block, item()).background,
                      }}
                    >
                      {calendarStatusLabel(props.block, item())}
                    </span>
                    <Show when={calendarChannelLabel(props.block, item())}>
                      <span class="rounded-full border border-[#e0d5ca] bg-[#fffaf3] px-2 py-1 text-11-medium text-[#5e5148]">
                        <span class="mr-1.5 inline-block h-1.5 w-1.5 rounded-full" style={{ "background-color": calendarChannelColor(props.block, item()) }} />
                        {calendarChannelLabel(props.block, item())}
                      </span>
                    </Show>
                  </div>
                </div>

                <Show when={selectableItems().length > 1}>
                  <div class="min-w-0 rounded-lg border border-[#eadfd4] bg-[#fff8ef] p-3">
                    <div class="text-11-medium uppercase tracking-[0.14em] text-[#8a7d72]">Items</div>
                    <div class="mt-2 flex max-h-48 flex-col gap-1 overflow-auto pr-1">
                      <For each={selectableItems()}>
                        {(calendarItem) => (
                          <button
                            type="button"
                            data-calendar-select-item={calendarItem.id}
                            class="flex min-w-0 items-center gap-2 rounded-md border px-2.5 py-2 text-left text-12-medium transition"
                            classList={{
                              "border-[#dd7b55] bg-[#fff0e7] text-[#211d19]": selectedID() === calendarItem.id,
                              "border-[#eadfd4] bg-[#fff8ef] text-[#5e5148] hover:bg-[#fff0e7]":
                                selectedID() !== calendarItem.id,
                            }}
                            onClick={() => setSelectedID(calendarItem.id)}
                          >
                            <span class="min-w-0 flex-1 truncate">{calendarItem.title}</span>
                            <span class="shrink-0 text-11-regular opacity-70">{calendarItem.startDate}</span>
                          </button>
                        )}
                      </For>
                    </div>
                  </div>
                </Show>

                <Show when={calendarMetaEntries(item()).length}>
                  <dl class="grid grid-cols-1 gap-2">
                    <For each={calendarMetaEntries(item())}>
                      {(entry) => (
                        <div class="rounded-md border border-[#eadfd4] bg-[#fff8ef] px-3 py-2">
                          <dt class="text-11-medium uppercase tracking-[0.1em] text-[#9a8d82]">{calendarMetaLabel(entry[0])}</dt>
                          <dd class="mt-1 text-12-regular text-[#5e5148]">{stringify(entry[1])}</dd>
                        </div>
                      )}
                    </For>
                  </dl>
                </Show>

                <Show when={visibleActions().length}>
                  <div class="flex flex-wrap gap-2">
                    <For each={visibleActions()}>
                      {(action) => {
                        const context = () =>
                          actionContext({
                            view: props.view,
                            block: props.block,
                            row: calendarItemRow(item()),
                            state: props.state,
                          })
                        const key = pluginActionStateKey({
                          blockId: props.block.id,
                          rowId: item().id,
                          actionId: action.id,
                        })
                        const disabled = () => actionDisabled(action, context())
                        return (
                          <Button
                            size="small"
                            variant={action.variant ?? "secondary"}
                            disabled={disabled() || props.state.pendingActionIds.has(key)}
                            title={disabled() ? (action.disabledReason ?? "Action unavailable") : undefined}
                            onClick={() => {
                              if (disabled() || !confirmAction(action)) return
                              props.onAction({
                                blockId: props.block.id,
                                rowId: item().id,
                                actionId: action.id,
                                confirmed: actionRequiresConfirmation(action),
                              })
                            }}
                          >
                            {props.state.pendingActionIds.has(key) ? `${action.label}...` : action.label}
                          </Button>
                        )
                      }}
                    </For>
                  </div>
                </Show>

                <Show when={!props.readonly && props.block.editForm}>
                  {(editForm) => (
                    <Show keyed when={item()}>
                      {(selectedCalendarItem) => (
                        <FormWidget
                          fields={editForm().fields}
                          sections={editForm().sections}
                          data={{ values: calendarItemValues(selectedCalendarItem), note: editForm().note }}
                          state={{
                            submitState: {
                              pending: props.state.pendingSubmitIds.has(props.block.id),
                              ...(props.state.submitState?.[props.block.id] ?? {}),
                            },
                          }}
                          submitLabel={editForm().submitLabel ?? "Update item"}
                          submitAction={editForm().submitAction}
                          onSubmit={(values) => {
                            const context = actionContext({
                              view: props.view,
                              block: props.block,
                              row: calendarItemRow(selectedCalendarItem),
                              values,
                              state: props.state,
                            })
                            const submitAction = editForm().submitAction
                            if (submitAction && !actionVisible(submitAction, context)) return
                            if (submitAction && actionDisabled(submitAction, context)) return
                            if (submitAction && !confirmAction({ ...submitAction, toolName: editForm().submitToolName })) return
                            props.onSubmit(
                              props.block.id,
                              values,
                              submitAction ? actionRequiresConfirmation(submitAction) : undefined,
                            )
                          }}
                        />
                      )}
                    </Show>
                  )}
                </Show>
              </div>
            )}
          </Show>
        </aside>
      </div>
    </div>
  )
}

function renderBlock(
  view: PluginPageView,
  block: Accessor<PluginBlock>,
  state: PluginViewState,
  onAction: (request: PluginActionRequest) => void | Promise<void>,
  onSubmit: (blockId: string, values: Record<string, unknown>, confirmed?: boolean) => void | Promise<void>,
  onUpload?: (blockId: string, values: { file: File; studio: string; kind?: string }) => Promise<unknown>,
  readonly?: boolean,
) {
  switch (block().type) {
    case "hero": {
      const item = () => block() as HeroBlock
      const brand = () => pluginBrand(view)
      const shellTitleHero = () => primaryHeroProvidesShellTitle(view, item())
      return (
        <PluginBlockFrame block={item()} full>
          <Show
            when={shellTitleHero()}
            fallback={
              <div class="flex flex-col gap-4 xl:flex-row xl:items-center xl:justify-between">
                <div class="flex flex-col gap-2">
                  <Show when={item().eyebrow}>
                    <div
                      data-plugin-hero-eyebrow
                      class={`w-fit rounded-md border px-2.5 py-1 text-11-medium uppercase tracking-[0.16em] ${heroEyebrowClass(item().eyebrow)}`}
                    >
                      {item().eyebrow}
                    </div>
                  </Show>
                  <h2 class="font-display italic text-[32px] leading-[1.08]" style={{ color: brand().accent }}>
                    {item().title}
                  </h2>
                  <Show when={item().description}>
                    <p class="max-w-2xl text-14-regular text-text-base">{item().description}</p>
                  </Show>
                </div>
                <Show when={item().imageUrl}>
                  <img src={item().imageUrl} alt={item().title} class="h-44 w-full rounded-2xl object-cover xl:w-80" />
                </Show>
              </div>
            }
          >
            <div data-plugin-hero-capabilities class="flex flex-col gap-2">
              <div class="text-12-medium uppercase tracking-[0.16em]" style={{ color: brand().ink }}>
                What OpenAnalyst can do
              </div>
              <Show when={item().description}>
                <p class="max-w-3xl text-14-regular leading-6 text-text-base">{item().description}</p>
              </Show>
              <Show when={item().imageUrl}>
                <img src={item().imageUrl} alt={item().title} class="mt-2 h-44 w-full rounded-lg object-cover xl:w-80" />
              </Show>
            </div>
          </Show>
        </PluginBlockFrame>
      )
    }
    case "markdown": {
      const item = () => block() as MarkdownBlock
      return (
        <PluginBlockFrame block={item()}>
          <Markdown text={item().content} />
        </PluginBlockFrame>
      )
    }
    case "stats": {
      const item = () => block() as StatsBlock
      return (
        <PluginBlockFrame block={item()} title={item().title}>
          {renderBlockActions({ block: item(), actions: item().actions, state, onAction, view })}
          <StatWidget data={item().items} />
        </PluginBlockFrame>
      )
    }
    case "calendar": {
      const item = () => block() as CalendarBlock
      return (
        <PluginBlockFrame block={item()} full>
          <CalendarBlockView
            view={view}
            block={item()}
            state={state}
            readonly={readonly}
            onAction={onAction}
            onSubmit={onSubmit}
          />
        </PluginBlockFrame>
      )
    }
    case "chart": {
      const item = () => block() as ChartBlock
      return (
        <PluginBlockFrame block={item()} title={item().title}>
          <ChartWidget data={item().data} chartType={item().chartType} options={item().options} />
        </PluginBlockFrame>
      )
    }
    case "table": {
      const item = () => block() as TableBlock
      const rowID = (row: Record<string, unknown>) => stringify(row[item().rowIdKey ?? "id"])
      return (
        <PluginBlockFrame block={item()} title={item().title} full={pluginBlockFull(item())}>
          <TableWidget
            data={item().rows}
            columns={item().columns}
            rowIdKey={item().rowIdKey}
            rowActions={item().rowActions}
            emptyState={item().emptyState}
            actionState={(action, row) => {
              const widgetAction = action as WidgetActionDeclaration
              const rowId = rowID(row)
              const context = actionContext({ view, block: item(), row, state })
              const key = pluginActionStateKey({ blockId: item().id, rowId, actionId: widgetAction.id })
              return {
                visible: actionVisible(widgetAction, context),
                disabled: actionDisabled(widgetAction, context),
                disabledReason: widgetAction.disabledReason,
                pending: state.pendingActionIds.has(key),
                error: state.actionState?.[key]?.error,
                result: state.actionState?.[key]?.result,
              }
            }}
            onRowAction={(action, row) => {
              const widgetAction = action as WidgetActionDeclaration
              const rowId = rowID(row)
              const context = actionContext({ view, block: item(), row, state })
              if (actionDisabled(widgetAction, context) || !confirmAction(widgetAction)) return
              onAction({
                blockId: item().id,
                rowId,
                actionId: widgetAction.id,
                confirmed: actionRequiresConfirmation(widgetAction),
              })
            }}
            expandedRowVisible={(row) => Boolean(item().expandedRows?.[rowID(row)]?.length)}
            renderExpandedRow={(row) => (
              <div class="flex flex-col gap-4 py-3">
                <For each={item().expandedRows?.[rowID(row)] ?? []}>
                  {(expanded) => renderBlock(view, () => expanded, state, onAction, onSubmit, onUpload, readonly)}
                </For>
              </div>
            )}
          />
        </PluginBlockFrame>
      )
    }
    case "list": {
      const item = () => block() as ListBlock
      return (
        <PluginBlockFrame block={item()} title={item().title} full={pluginBlockFull(item())}>
          {renderBlockActions({ block: item(), actions: item().actions, state, onAction, view })}
          <ListWidget
            data={item().items}
            emptyState={item().emptyState}
            layout={isAvailableToolsBlock(item()) ? "tool-grid" : "stack"}
            itemActions={item().itemActions}
            actionState={(action, listItem) => {
              const widgetAction = action as WidgetActionDeclaration
              const row = listItem as Record<string, unknown>
              const rowId = stringify(row.id ?? row.title)
              const context = actionContext({ view, block: item(), row, state })
              const key = pluginActionStateKey({ blockId: item().id, rowId, actionId: widgetAction.id })
              return {
                visible: actionVisible(widgetAction, context),
                disabled: actionDisabled(widgetAction, context),
                disabledReason: widgetAction.disabledReason,
                pending: state.pendingActionIds.has(key),
              }
            }}
            onItemAction={(action, listItem) => {
              const widgetAction = action as WidgetActionDeclaration
              const row = listItem as Record<string, unknown>
              const rowId = stringify(row.id ?? row.title)
              const context = actionContext({ view, block: item(), row, state })
              if (actionDisabled(widgetAction, context) || !confirmAction(widgetAction)) return
              onAction({
                blockId: item().id,
                rowId,
                actionId: widgetAction.id,
                confirmed: actionRequiresConfirmation(widgetAction),
              })
            }}
          />
        </PluginBlockFrame>
      )
    }
    case "detail": {
      const item = () => block() as DetailBlock
      const actions = () =>
        (item().actions ?? []).filter((action) => actionVisible(action, actionContext({ view, block: item(), state })))
      return (
        <PluginBlockFrame block={item()} title={item().title}>
          <DetailWidget
            data={{
              sections: item().sections.map((section) => ({
                title: section.title,
                items: section.items.map((item) => ({
                  label: item.label,
                  value: stringify(item.value),
                  hint: item.hint,
                })),
              })),
            }}
            actions={
              readonly
                ? []
                : actions().map((action) => ({
                    ...action,
                    disabled: actionDisabled(action, actionContext({ view, block: item(), state })),
                    disabledReason: action.disabledReason,
                  }))
            }
            state={{
              actionState: Object.fromEntries(
                actions().map((action) => [
                  action.id,
                  {
                    pending: state.pendingActionIds.has(
                      pluginActionStateKey({ blockId: item().id, actionId: action.id }),
                    ),
                    ...(state.actionState?.[pluginActionStateKey({ blockId: item().id, actionId: action.id })] ?? {}),
                  },
                ]),
              ),
            }}
            onAction={(actionId) => {
              const action = actions().find((entry) => entry.id === actionId)
              if (!action || !confirmAction(action)) return
              onAction({
                blockId: item().id,
                actionId,
                confirmed: actionRequiresConfirmation(action),
              })
            }}
          />
        </PluginBlockFrame>
      )
    }
    case "form": {
      const item = () => block() as FormBlock
      if (readonly) {
        return (
          <Card class="rounded-2xl border border-border-weak-base bg-background-base p-5">
            <Show when={item().title}>
              <h2 class="mb-4 text-16-medium text-text-strong">{item().title}</h2>
            </Show>
            <div data-form-readonly class="flex flex-col gap-4">
              <div class="grid grid-cols-1 gap-3 md:grid-cols-2">
                <For each={item().fields}>
                  {(field) => (
                    <div class="rounded-lg border border-border-weak-base bg-background-secondary p-3">
                      <div class="text-12-medium uppercase tracking-[0.12em] text-text-base">{field.label}</div>
                      <div class="mt-2 text-13-regular text-text-strong">{stringify(item().values?.[field.key])}</div>
                      <Show when={field.description}>
                        <div class="mt-1 text-12-regular text-text-base">{field.description}</div>
                      </Show>
                    </div>
                  )}
                </For>
              </div>
              <Show when={item().note}>
                <div class="rounded-lg border border-border-weak-base bg-background-secondary px-4 py-3 text-13-regular text-text-base">
                  {item().note}
                </div>
              </Show>
            </div>
          </Card>
        )
      }
      return (
        <PluginBlockFrame block={item()} title={item().title}>
          <FormWidget
            fields={item().fields}
            sections={item().sections}
            data={{
              values: item().values,
              note: item().note,
            }}
            state={{
              submitState: {
                pending: state.pendingSubmitIds.has(item().id),
                ...(state.submitState?.[item().id] ?? {}),
              },
            }}
            submitLabel={item().submitLabel}
            submitAction={item().submitAction}
            onSubmit={(values) => {
              const context = actionContext({ view, block: item(), values, state })
              const submitAction = item().submitAction
              if (submitAction && !actionVisible(submitAction, context)) return
              if (submitAction && actionDisabled(submitAction, context)) return
              if (submitAction && !confirmAction({ ...submitAction, toolName: item().submitToolName })) return
              onSubmit(item().id, values, submitAction ? actionRequiresConfirmation(submitAction) : undefined)
            }}
          />
        </PluginBlockFrame>
      )
    }
    case "todo": {
      const item = () => block() as TodoBlock
      return (
        <PluginBlockFrame block={item()}>
          <TodoBlockView
            block={item()}
            pending={state.pendingSubmitIds.has(item().id)}
            submitState={state.submitState?.[item().id]}
            onSubmit={(values) => onSubmit(item().id, values)}
          />
        </PluginBlockFrame>
      )
    }
    case "card_grid": {
      const item = () => block() as CardGridBlock
      return (
        <PluginBlockFrame block={item()} title={item().title}>
          {renderBlockActions({ block: item(), actions: item().actions, state, onAction, view })}
          <div class="grid grid-cols-1 gap-4 md:grid-cols-2">
            <For
              each={item().items}
              fallback={
                <div class="rounded-lg border border-dashed border-border-weak-base bg-background-secondary px-3 py-4 text-center text-xs text-text-dimmed md:col-span-2">
                  {item().emptyState?.title ?? "No items yet"}
                </div>
              }
            >
              {(item) => (
                <div class="group relative flex flex-col overflow-hidden rounded-2xl border border-border-weak-base bg-gradient-to-br from-background-base to-background-secondary/50 p-4 transition-all duration-200 hover:-translate-y-0.5 hover:border-border-base hover:shadow-[0_12px_34px_-16px_rgba(15,23,42,0.3)]">
                  <Show when={item.imageUrl}>
                    <Show
                      when={item.mediaKind === "video"}
                      fallback={<img src={item.imageUrl} alt={item.title} class="mb-3 h-44 w-full rounded-xl bg-background-base object-cover" />}
                    >
                      <video src={item.imageUrl} muted controls class="mb-3 h-44 w-full rounded-xl bg-black object-contain" />
                    </Show>
                  </Show>
                  <Show when={item.eyebrow}>
                    <span
                      class="w-fit rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.1em]"
                      style={{
                        color: "var(--oa-plugin-accent, #4f46e5)",
                        background: "color-mix(in srgb, var(--oa-plugin-accent, #6366f1) 13%, transparent)",
                      }}
                    >
                      {item.eyebrow}
                    </span>
                  </Show>
                  <div class="mt-1.5 text-16-medium text-text-strong">{item.title}</div>
                  <Show when={item.description}>
                    <p class="mt-2 text-13-regular leading-relaxed text-text-base">{item.description}</p>
                  </Show>
                  <Show when={item.href}>
                    <a
                      href={item.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={(event) => {
                        // Open in the user's SYSTEM BROWSER, never navigate the app
                        // window to the page (that replaced the whole app with the
                        // page — no chrome, no way back). window.open is routed to
                        // shell.openExternal by the desktop setWindowOpenHandler.
                        event.preventDefault()
                        if (item.href) window.open(item.href, "_blank", "noopener,noreferrer")
                      }}
                      class="mt-3 inline-flex w-fit items-center gap-1 text-13-medium transition-colors"
                      style={{ color: "var(--oa-plugin-accent, #4f46e5)" }}
                    >
                      Open <span aria-hidden="true">→</span>
                    </a>
                  </Show>
                  <Show when={(block() as CardGridBlock).itemActions?.length}>
                    <div class="mt-4 flex flex-wrap gap-2">
                      <For
                        each={((block() as CardGridBlock).itemActions ?? []).filter((action) =>
                          actionVisible(
                            action,
                            actionContext({ view, block: block(), row: item as Record<string, unknown>, state }),
                          ),
                        )}
                      >
                        {(action) => {
                          const rowId = stringify(item.id)
                          const context = () =>
                            actionContext({ view, block: block(), row: item as Record<string, unknown>, state })
                          const key = pluginActionStateKey({ blockId: block().id, rowId, actionId: action.id })
                          const disabled = () => actionDisabled(action, context())
                          return (
                            <Button
                              size="small"
                              variant={action.variant ?? "secondary"}
                              disabled={disabled() || state.pendingActionIds.has(key)}
                              title={disabled() ? (action.disabledReason ?? "Action unavailable") : undefined}
                              onClick={() => {
                                if (disabled() || !confirmAction(action)) return
                                onAction({
                                  blockId: block().id,
                                  rowId,
                                  actionId: action.id,
                                  confirmed: actionRequiresConfirmation(action),
                                })
                              }}
                            >
                              {state.pendingActionIds.has(key) ? `${action.label}...` : action.label}
                            </Button>
                          )
                        }}
                      </For>
                    </div>
                  </Show>
                </div>
              )}
            </For>
          </div>
        </PluginBlockFrame>
      )
    }
    case "gallery": {
      const item = () => block() as GalleryBlock
      return (
        <PluginBlockFrame block={item()} title={item().title}>
          <div class="grid grid-cols-1 gap-4 md:grid-cols-2">
            <For each={item().items}>
              {(item) => (
                <figure class="overflow-hidden rounded-2xl border border-border-weak-base bg-background-secondary">
                  <img
                    src={item.src}
                    alt={item.alt ?? item.title ?? "Gallery image"}
                    class="h-56 w-full object-cover"
                  />
                  <Show when={item.title || item.description}>
                    <figcaption class="flex flex-col gap-1 p-3">
                      <Show when={item.title}>
                        <span class="text-14-medium text-text-strong">{item.title}</span>
                      </Show>
                      <Show when={item.description}>
                        <span class="text-12-regular text-text-base">{item.description}</span>
                      </Show>
                    </figcaption>
                  </Show>
                </figure>
              )}
            </For>
          </div>
        </PluginBlockFrame>
      )
    }
    case "generative":
      return readonly ? (
        <fieldset disabled class="min-w-0 border-0 p-0 m-0">
          <GenerativeBlockView
            block={block() as GenerativeBlock}
            pending={state.pendingSubmitIds.has(block().id)}
            readonly
            onSubmit={onSubmit}
            onUpload={onUpload}
          />
        </fieldset>
      ) : (
        <GenerativeBlockView
          block={block() as GenerativeBlock}
          pending={state.pendingSubmitIds.has(block().id)}
          readonly={readonly}
          onSubmit={onSubmit}
          onUpload={onUpload}
        />
      )
    case "generative_stage":
      return <GenerationStageVisual job={(block() as GenerativeStageBlock).job} />
    case "artifact_editor": {
      // The old artifact-editor route was removed. The Canvas artifact +
      // annotation cover editing now. If a legacy block still arrives, show a
      // read-only sandboxed preview of its HTML rather than a visual editor.
      const ae = block() as { id: string; title?: string; html?: string }
      const preview = (
        <iframe
          title="Artifact preview"
          sandbox="allow-scripts allow-popups allow-forms"
          srcdoc={ae.html ?? ""}
          class="min-h-[480px] w-full rounded-lg border border-border-weak-base bg-white"
        />
      )
      return readonly ? (
        <fieldset disabled class="min-w-0 border-0 p-0 m-0">
          {preview}
        </fieldset>
      ) : (
        <PluginBlockFrame block={block()} title={ae.title} full>
          {preview}
        </PluginBlockFrame>
      )
    }
    case "visual_pipeline":
      return readonly ? (
        <fieldset disabled class="min-w-0 border-0 p-0 m-0">
          <VisualPipelineBlockView
            block={block() as VisualPipelineBlock}
            pending={state.pendingSubmitIds.has(block().id)}
            readonly
            onSubmit={onSubmit}
          />
        </fieldset>
      ) : (
        <PluginBlockFrame block={block()} title={(block() as VisualPipelineBlock).title} full>
          <VisualPipelineBlockView
            block={block() as VisualPipelineBlock}
            pending={state.pendingSubmitIds.has(block().id)}
            onSubmit={onSubmit}
          />
        </PluginBlockFrame>
      )
    case "notice": {
      const item = () => block() as NoticeBlock
      const tone = () =>
        item().tone === "error"
          ? "text-text-danger"
          : item().tone === "warning"
            ? "text-text-warning"
            : item().tone === "success"
              ? "text-text-success"
              : "text-text-base"
      return (
        <PluginBlockFrame block={item()} title={item().title}>
          <p class={`text-14-regular ${tone()}`}>{item().message}</p>
          <div class="mt-4">
            {renderBlockActions({ block: item(), actions: item().actions, state, onAction, view })}
          </div>
        </PluginBlockFrame>
      )
    }
    case "preview": {
      const item = () => block() as PreviewBlock
      return (
        <PluginBlockFrame block={item()} title={item().title} full={pluginBlockFull(item())}>
          <PreviewContent block={item()} />
        </PluginBlockFrame>
      )
    }
    case "registry": {
      // Phase 4 dispatch: plugins can declare any registry-backed
      // component as a block; see ./registry-block.tsx for the map.
      const item = () => block() as RegistryBlock
      const runtime = () =>
        item().submitToolName
          ? {
              blockId: item().id,
              pending: state.pendingSubmitIds.has(item().id),
              readonly,
              submits: item().submits ?? [],
              submitState: state.submitState?.[item().id],
              context: actionContext({ view, block: item(), state }),
              onSubmit: (values: Record<string, unknown>) => onSubmit(item().id, values),
            }
          : undefined
      const body = () => <RegistryBlockRenderer block={item() as unknown as PluginRegistryBlock} runtime={runtime()} />
      if (item().title || item().actions?.length) {
        return (
          <PluginBlockFrame block={item()} title={item().title} full={pluginBlockFull(item())}>
            {renderBlockActions({ block: item(), actions: item().actions, state, onAction, view })}
            {body()}
          </PluginBlockFrame>
        )
      }
      return (
        <div
          data-plugin-block={item().id}
          data-plugin-block-type={item().type}
          class="min-w-0"
          classList={{
            "xl:col-span-2": pluginBlockFull(item()),
          }}
        >
          {body()}
        </div>
      )
    }
  }
}

function stringify(value: unknown) {
  if (typeof value === "string") return value
  if (typeof value === "number" || typeof value === "boolean") return String(value)
  return JSON.stringify(value, null, 2)
}

function PreviewContent(props: { block: PreviewBlock }) {
  const content = () => props.block.content
  const previewFrame =
    "min-h-[520px] h-[78vh] max-h-[900px] w-full rounded-lg border border-border-weak-base bg-background-base"
  return (
    <Show
      when={content() !== undefined && content() !== null && stringify(content()).trim()}
      fallback={
        <div class="rounded-lg border border-dashed border-border-weak-base bg-background-secondary px-3 py-4 text-center text-xs text-text-dimmed">
          {props.block.emptyState?.title ?? "No preview available"}
        </div>
      }
    >
      <Show when={props.block.mode === "html"}>
        <iframe
          title={props.block.title ?? props.block.id}
          srcdoc={String(content() ?? "")}
          sandbox="allow-forms allow-popups allow-scripts"
          class={previewFrame}
        />
      </Show>
      <Show when={props.block.mode === "markdown"}>
        <Markdown text={String(content() ?? "")} />
      </Show>
      <Show when={props.block.mode === "url"}>
        <iframe
          title={props.block.title ?? props.block.id}
          src={String(content() ?? "")}
          sandbox="allow-forms allow-popups allow-same-origin"
          class={previewFrame}
        />
      </Show>
      <Show when={props.block.mode === "json"}>
        <pre class={`${previewFrame} overflow-auto bg-background-secondary p-3 text-12-regular text-text-base`}>
          {stringify(content())}
        </pre>
      </Show>
    </Show>
  )
}

function TodoBlockView(props: {
  block: TodoBlock
  pending: boolean
  submitState?: Omit<SubmitState, "pending">
  onSubmit: (values: Record<string, unknown>) => void | Promise<void>
}) {
  const [title, setTitle] = createSignal("")

  function create() {
    const value = title().trim()
    if (!value) return
    props.onSubmit({ command: "todo.create", title: value })
    setTitle("")
  }

  return (
    <div data-todo-block={props.block.id} class="flex flex-col gap-4">
      <Show when={props.block.title}>
        <h2 class="text-16-medium text-text-strong">{props.block.title}</h2>
      </Show>
      <div class="flex flex-col gap-2">
        <For each={props.block.items} fallback={<div class="text-13-regular text-text-base">No todos yet</div>}>
          {(item) => (
            <div class="flex items-center justify-between gap-3 rounded-lg border border-border-weak-base bg-background-secondary px-3 py-2">
              <button
                type="button"
                data-todo-toggle={item.id}
                disabled={props.pending}
                class={`h-5 w-5 shrink-0 rounded border ${item.status === "done" ? "border-border-strong bg-text-success" : "border-border-weak-base bg-background-base"}`}
                on:click={() => props.onSubmit({ command: "todo.toggle", id: item.id })}
                aria-label={`Toggle ${item.title}`}
              />
              <div class="min-w-0 flex-1">
                <div
                  class={`truncate text-13-medium ${item.status === "done" ? "text-text-base line-through" : "text-text-strong"}`}
                >
                  {item.title}
                </div>
                <div class="mt-1 flex flex-wrap gap-2 text-11-regular text-text-base">
                  <span>{item.status}</span>
                  <Show when={item.priority}>
                    <span>{item.priority}</span>
                  </Show>
                  <Show when={item.dueAt}>
                    <span>{item.dueAt}</span>
                  </Show>
                  <Show when={item.source}>
                    <span>{item.source}</span>
                  </Show>
                </div>
              </div>
              <button
                type="button"
                data-todo-delete={item.id}
                disabled={props.pending}
                class="rounded-md border border-border-weak-base px-2 py-1 text-12-medium text-text-base disabled:opacity-60"
                on:click={() => props.onSubmit({ command: "todo.delete", id: item.id })}
              >
                Delete
              </button>
            </div>
          )}
        </For>
      </div>
      <form
        class="flex flex-col gap-2 sm:flex-row"
        on:submit={(event) => {
          event.preventDefault()
          create()
        }}
      >
        <input
          data-todo-title={props.block.id}
          value={title()}
          placeholder="Add todo"
          class="min-w-0 flex-1 rounded-md border border-border-weak-base bg-background-base px-3 py-2 text-13-regular text-text-strong"
          on:input={(event) => setTitle(event.currentTarget.value)}
        />
        <button
          type="submit"
          data-todo-create={props.block.id}
          disabled={props.pending}
          class="rounded-md border border-border-strong bg-background-base px-3 py-2 text-13-medium text-text-strong disabled:opacity-60"
        >
          Add
        </button>
      </form>
      <Show when={props.submitState?.error || props.submitState?.result}>
        <pre class="rounded-md border border-border-weak-base bg-background-secondary p-3 text-11-regular text-text-base">
          {props.submitState?.error ?? stringify(props.submitState?.result)}
        </pre>
      </Show>
    </div>
  )
}

function recordItems(value: unknown) {
  return Array.isArray(value)
    ? value.filter(
        (item): item is Record<string, unknown> => typeof item === "object" && item !== null && !Array.isArray(item),
      )
    : []
}

function stringItems(value: unknown) {
  return Array.isArray(value) ? value.map(stringify).filter((item): item is string => Boolean(item)) : []
}


function asRecord(value: unknown): Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value) ? (value as Record<string, unknown>) : {}
}

function asArray<T>(value: unknown): T[] {
  return Array.isArray(value) ? (value as T[]) : []
}


// Read-only cell formatting for the records table — turns a raw stored value into
// a display string per the field type (currency/date/select label/boolean/list).








